import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  playCosmicRadarBeep, 
  playCorrectChime, 
  playWrongBuzzer, 
  playWinFanfare, 
  playLaunchChime 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiMaximize2, FiMinimize2, FiRefreshCw, FiZap, FiCompass, FiCheckCircle } = FiIcons;

// Accurate and engaging STEM planetary data
const CELESTIAL_BODIES = [
  {
    id: 'sun',
    name: 'The Sun (Surya)',
    tagline: 'Heart of our Solar System',
    type: 'Yellow Dwarf Star',
    diameter: '1,392,700 km (109x Earth)',
    distanceFromSun: '0 AU (Center)',
    orbitDays: '230 Million Earth Years around Milky Way',
    dayLength: '27 Earth Days',
    temp: '5,500°C (Surface) / 15M°C (Core)',
    gravity: '274.0 m/s² (28x Earth)',
    moons: '8 Planets & Billions of Comets',
    isroMission: 'Aditya-L1 (L1 Lagrange Point Solar Observatory)',
    colorGradient: 'from-amber-400 via-orange-500 to-red-600',
    glowColor: 'rgba(245, 158, 11, 0.6)',
    funFact: 'The Sun contains 99.86% of all the mass in the entire solar system!',
    icon: '☀️',
    scaleRatio: 1.6
  },
  {
    id: 'mercury',
    name: 'Mercury (Budha)',
    tagline: 'The Swift Iron Messenger',
    type: 'Terrestrial Rocky Planet',
    diameter: '4,879 km',
    distanceFromSun: '57.9 Million km (0.39 AU)',
    orbitDays: '88 Earth Days (Shortest Year)',
    dayLength: '59 Earth Days',
    temp: '-180°C (Night) to +430°C (Day)',
    gravity: '3.7 m/s² (0.38x Earth)',
    moons: '0 Moons',
    isroMission: 'Observed by Space Telescope Network',
    colorGradient: 'from-slate-300 via-stone-400 to-zinc-600',
    glowColor: 'rgba(168, 162, 158, 0.4)',
    funFact: 'Mercury has no atmosphere to trap heat, so temperatures swing by 600°C between day and night!',
    icon: '🌑',
    scaleRatio: 0.7
  },
  {
    id: 'venus',
    name: 'Venus (Shukra)',
    tagline: 'Earth’s Runaway Greenhouse Twin',
    type: 'Terrestrial Rocky Planet',
    diameter: '12,104 km',
    distanceFromSun: '108.2 Million km (0.72 AU)',
    orbitDays: '225 Earth Days',
    dayLength: '243 Earth Days (Retrograde)',
    temp: '465°C (Hottest Planet)',
    gravity: '8.87 m/s² (0.90x Earth)',
    moons: '0 Moons',
    isroMission: 'Shukrayaan (Planned Venus Orbiter)',
    colorGradient: 'from-yellow-200 via-amber-400 to-orange-600',
    glowColor: 'rgba(251, 191, 36, 0.5)',
    funFact: 'Venus rotates backwards! Its day is longer than its entire year, and the sun rises in the west.',
    icon: '🌕',
    scaleRatio: 0.95
  },
  {
    id: 'earth',
    name: 'Earth (Prithvi)',
    tagline: 'The Oasis of Life & Water',
    type: 'Terrestrial Living Planet',
    diameter: '12,742 km',
    distanceFromSun: '149.6 Million km (1.0 AU)',
    orbitDays: '365.25 Days',
    dayLength: '24 Hours',
    temp: '15°C (Average Global)',
    gravity: '9.8 m/s² (Standard 1G)',
    moons: '1 Moon (Luna)',
    isroMission: 'Gaganyaan (Crewed Spaceflight) & IRS Satellites',
    colorGradient: 'from-blue-400 via-emerald-400 to-cyan-600',
    glowColor: 'rgba(56, 189, 248, 0.6)',
    funFact: 'Earth is the only known celestial body in the universe with liquid water oceans and plate tectonics.',
    icon: '🌍',
    scaleRatio: 1.0
  },
  {
    id: 'mars',
    name: 'Mars (Mangal)',
    tagline: 'The Rust-Colored Red Frontier',
    type: 'Terrestrial Desert Planet',
    diameter: '6,779 km',
    distanceFromSun: '227.9 Million km (1.52 AU)',
    orbitDays: '687 Earth Days',
    dayLength: '24h 37m (1 Sol)',
    temp: '-63°C (Average)',
    gravity: '3.72 m/s² (0.38x Earth)',
    moons: '2 Moons (Phobos & Deimos)',
    isroMission: 'Mangalyaan (Mars Orbiter Mission - MOM)',
    colorGradient: 'from-red-400 via-rose-600 to-orange-700',
    glowColor: 'rgba(244, 63, 94, 0.5)',
    funFact: 'Home to Olympus Mons, an extinct volcano three times higher than Mount Everest!',
    icon: '🔴',
    scaleRatio: 0.8
  },
  {
    id: 'jupiter',
    name: 'Jupiter (Brihaspati)',
    tagline: 'King of Gas Giants',
    type: 'Gas Giant (Hydrogen & Helium)',
    diameter: '139,820 km (11x Earth)',
    distanceFromSun: '778.5 Million km (5.20 AU)',
    orbitDays: '11.86 Earth Years',
    dayLength: '9h 56m (Fastest Rotation)',
    temp: '-110°C (Cloud Tops)',
    gravity: '24.79 m/s² (2.5x Earth)',
    moons: '95 Moons (Ganymede, Europa, Io, Callisto)',
    isroMission: 'Juno & Deep Space Radar Tracking',
    colorGradient: 'from-amber-200 via-orange-300 to-amber-700',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    funFact: 'Jupiter’s famous Great Red Spot is a gargantuan super-storm that has been raging for over 350 years!',
    icon: '🪐',
    scaleRatio: 1.45
  },
  {
    id: 'saturn',
    name: 'Saturn (Shani)',
    tagline: 'Jewel of the Solar Rings',
    type: 'Gas Giant',
    diameter: '116,460 km (9.5x Earth)',
    distanceFromSun: '1.43 Billion km (9.58 AU)',
    orbitDays: '29.45 Earth Years',
    dayLength: '10h 33m',
    temp: '-140°C',
    gravity: '10.44 m/s² (1.06x Earth)',
    moons: '146 Confirmed Moons (Titan & Enceladus)',
    isroMission: 'Cassini Mission Data Science',
    colorGradient: 'from-yellow-100 via-amber-200 to-amber-600',
    glowColor: 'rgba(253, 224, 71, 0.4)',
    funFact: 'Saturn is so light and full of gas that if you had an ocean large enough, Saturn would literally float in water!',
    icon: '🪐',
    scaleRatio: 1.35
  },
  {
    id: 'uranus',
    name: 'Uranus (Arun)',
    tagline: 'The Tilted Ice Giant',
    type: 'Ice Giant (Water, Ammonia, Methane)',
    diameter: '50,724 km (4x Earth)',
    distanceFromSun: '2.87 Billion km (19.2 AU)',
    orbitDays: '84 Earth Years',
    dayLength: '17h 14m',
    temp: '-195°C (Freezing Atmosphere)',
    gravity: '8.69 m/s² (0.89x Earth)',
    moons: '28 Moons',
    isroMission: 'Deep Space Atmospheric Studies',
    colorGradient: 'from-teal-200 via-cyan-400 to-blue-600',
    glowColor: 'rgba(34, 211, 238, 0.5)',
    funFact: 'Uranus rolls on its side like a bowling ball with an axial tilt of 98 degrees!',
    icon: '🌐',
    scaleRatio: 1.1
  },
  {
    id: 'neptune',
    name: 'Neptune (Varun)',
    tagline: 'The Supersonic Blue Whirlwind',
    type: 'Ice Giant',
    diameter: '49,244 km',
    distanceFromSun: '4.50 Billion km (30.1 AU)',
    orbitDays: '165 Earth Years',
    dayLength: '16h 6m',
    temp: '-201°C (Extreme Deep Ice)',
    gravity: '11.15 m/s² (1.14x Earth)',
    moons: '16 Moons (Triton)',
    isroMission: 'Voyager Interstellar Trajectory Data',
    colorGradient: 'from-blue-500 via-indigo-600 to-sky-700',
    glowColor: 'rgba(99, 102, 241, 0.5)',
    funFact: 'Neptune has the fastest supersonic winds in the solar system, gusting up to 2,100 km/h!',
    icon: '🔵',
    scaleRatio: 1.1
  }
];

const STEM_QUIZ_QUESTIONS = [
  {
    q: "Which planet is famously known as the 'Red Planet' due to iron-oxide rust on its surface?",
    options: ["Venus", "Mars", "Jupiter", "Mercury"],
    correctIndex: 1,
    insight: "Mars' soil is rich in oxidized iron dust, giving it an iconic reddish tint."
  },
  {
    q: "What is the name of India's dedicated solar observatory placed at the L1 Lagrange point?",
    options: ["Chandrayaan-3", "Mangalyaan", "Aditya-L1", "Gaganyaan"],
    correctIndex: 2,
    insight: "Aditya-L1 monitors the Sun 24x7 without any eclipses from 1.5 million km away!"
  },
  {
    q: "Which gas giant planet in our solar system has over 140 moons and magnificent ice-ring arcs?",
    options: ["Saturn", "Uranus", "Neptune", "Jupiter"],
    correctIndex: 0,
    insight: "Saturn holds the planetary record with 146 confirmed moons!"
  }
];

export default function SolarSystemFlow() {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [selectedPlanet, setSelectedPlanet] = useState(CELESTIAL_BODIES[3]); // Earth default
  const [activeTab, setActiveTab] = useState('orrery'); // 'orrery' | 'telemetry' | 'quiz' | 'pass'
  const [autoRotate, setAutoRotate] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDayMode, setIsDayMode] = useState(false);

  // Quiz state
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [quizScore, setQuizScore] = useState(0);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);

  // Student pass details
  const [studentName, setStudentName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [gradeClass, setGradeClass] = useState('Grade 6');
  const [hasClaimedPass, setHasClaimedPass] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');

  // Auto-tour rotation for live stage projectors
  useEffect(() => {
    if (!autoRotate || activeTab !== 'orrery') return;
    const interval = setInterval(() => {
      setSelectedPlanet((prev) => {
        const currentIndex = CELESTIAL_BODIES.findIndex(b => b.id === prev.id);
        const nextIndex = (currentIndex + 1) % CELESTIAL_BODIES.length;
        playCosmicRadarBeep(650 + nextIndex * 60);
        return CELESTIAL_BODIES[nextIndex];
      });
    }, 7000);
    return () => clearInterval(interval);
  }, [autoRotate, activeTab]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const handleSelectPlanet = (planet) => {
    setAutoRotate(false);
    setSelectedPlanet(planet);
    playCosmicRadarBeep(880);
  };

  const handleAnswerSubmit = (optionIdx) => {
    if (showAnswerFeedback) return;
    setSelectedOption(optionIdx);
    setShowAnswerFeedback(true);

    const isCorrect = optionIdx === STEM_QUIZ_QUESTIONS[quizIndex].correctIndex;
    if (isCorrect) {
      playCorrectChime();
      setQuizScore(prev => prev + 1);
    } else {
      playWrongBuzzer();
    }

    setTimeout(() => {
      setShowAnswerFeedback(false);
      setSelectedOption(null);
      if (quizIndex < STEM_QUIZ_QUESTIONS.length - 1) {
        setQuizIndex(prev => prev + 1);
      } else {
        // Complete quiz -> unlock pass
        playWinFanfare();
        setActiveTab('pass');
        const code = 'GALAXY-' + Math.random().toString(36).substring(2, 7).toUpperCase();
        setVoucherCode(code);
      }
    }, 2000);
  };

  const handleGenerateCertificate = (e) => {
    e.preventDefault();
    if (!studentName.trim()) return;

    setHasClaimedPass(true);
    playLaunchChime();

    // Broadcast winner record into system
    try {
      const existing = JSON.parse(localStorage.getItem('gamifycenter_school_records') || '[]');
      const newRecord = {
        id: 'STEM-SOLAR-' + Date.now(),
        studentName: studentName.trim(),
        schoolName: schoolName.trim() || 'Cosmic Academy',
        gameTitle: 'Solar System Planetary Orrery',
        category: 'Space & Astronomy STEM',
        score: `${quizScore + 1}/${STEM_QUIZ_QUESTIONS.length} Questions Correct`,
        prizeWon: 'Junior Galactic Navigator Badge & Astronomy Star Pass',
        voucherCode: voucherCode || ('GALAXY-' + Math.floor(1000 + Math.random() * 9000)),
        timestamp: new Date().toISOString()
      };
      existing.unshift(newRecord);
      localStorage.setItem('gamifycenter_school_records', JSON.stringify(existing.slice(0, 100)));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDayMode ? 'bg-slate-100 text-slate-800' : 'bg-[#080d1a] text-slate-100'
    } flex flex-col font-sans select-none overflow-x-hidden`}>

      {/* Top Telemetry / Stage Header */}
      <header className={`px-4 sm:px-6 py-3 border-b flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30 backdrop-blur-md ${
        isDayMode ? 'bg-white/80 border-slate-200 shadow-sm' : 'bg-slate-900/80 border-slate-800 shadow-lg shadow-black/40'
      }`}>
        <div className="flex items-center gap-3">
          <Link 
            to={exitRoute} 
            title={exitTitle}
            className={`p-2 rounded-xl border flex items-center gap-1.5 text-xs font-bold transition-all ${
              isDayMode ? 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200' : 'bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700'
            }`}
          >
            <SafeIcon icon={FiHome} className="text-sm" />
            <span className="hidden sm:inline">Portal</span>
          </Link>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🪐</span>
              <h1 className="text-base sm:text-lg font-black tracking-wide bg-gradient-to-r from-amber-400 via-sky-300 to-indigo-400 bg-clip-text text-transparent">
                Solar System Planetary Orrery
              </h1>
              <span className="hidden md:inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30">
                Live STEM Stage / Kiosk
              </span>
            </div>
            <p className="text-[11px] text-slate-400 hidden sm:block">
              Interactive 3D Planetary Physics, ISRO Space Missions & Gravity Explorer
            </p>
          </div>
        </div>

        {/* Tab Controls & Settings */}
        <div className="flex items-center gap-2">
          {/* View Modes */}
          <div className={`p-1 rounded-xl border flex items-center text-xs font-bold ${
            isDayMode ? 'bg-slate-200 border-slate-300' : 'bg-slate-800/90 border-slate-700'
          }`}>
            <button
              onClick={() => setActiveTab('orrery')}
              className={`px-3 py-1 rounded-lg transition-all ${
                activeTab === 'orrery' ? 'bg-amber-500 text-slate-950 font-black shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
            >
              🔭 3D Orrery
            </button>
            <button
              onClick={() => setActiveTab('telemetry')}
              className={`px-3 py-1 rounded-lg transition-all ${
                activeTab === 'telemetry' ? 'bg-amber-500 text-slate-950 font-black shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
            >
              📊 Telemetry
            </button>
            <button
              onClick={() => setActiveTab('quiz')}
              className={`px-3 py-1 rounded-lg transition-all ${
                activeTab === 'quiz' ? 'bg-amber-500 text-slate-950 font-black shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
            >
              ⚡ Cadet Quiz
            </button>
          </div>

          {/* Auto Tour Toggle */}
          <button
            onClick={() => setAutoRotate(!autoRotate)}
            className={`px-2.5 py-1.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-1.5 ${
              autoRotate 
                ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' 
                : isDayMode ? 'bg-slate-200 border-slate-300 text-slate-600' : 'bg-slate-800 border-slate-700 text-slate-400'
            }`}
            title="Auto-tour stages through each planet every 7 seconds"
          >
            <SafeIcon icon={FiRefreshCw} className={`text-xs ${autoRotate ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">{autoRotate ? 'Tour Active' : 'Tour Off'}</span>
          </button>

          {/* Day / Night */}
          <button
            onClick={() => setIsDayMode(!isDayMode)}
            className={`p-2 rounded-xl border transition-all ${
              isDayMode ? 'bg-slate-200 border-slate-300 text-slate-700' : 'bg-slate-800 border-slate-700 text-amber-300'
            }`}
            title="Toggle Day/Night View"
          >
            <SafeIcon icon={isDayMode ? FiMoon : FiSun} className="text-sm" />
          </button>

          {/* Fullscreen for Classroom Projectors */}
          <button
            onClick={toggleFullscreen}
            className={`p-2 rounded-xl border transition-all ${
              isDayMode ? 'bg-slate-200 border-slate-300 text-slate-700' : 'bg-slate-800 border-slate-700 text-slate-300 hover:text-white'
            }`}
            title="Stage Projector Fullscreen"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="text-sm" />
          </button>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 p-3 sm:p-6 max-w-7xl w-full mx-auto flex flex-col gap-5">

        {/* Planet Carousel Navigation Strip */}
        <div className={`p-2 rounded-2xl border flex items-center gap-2 overflow-x-auto scrollbar-thin ${
          isDayMode ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-900/60 border-slate-800/80'
        }`}>
          {CELESTIAL_BODIES.map((planet) => {
            const isSelected = selectedPlanet.id === planet.id;
            return (
              <button
                key={planet.id}
                onClick={() => handleSelectPlanet(planet)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl whitespace-nowrap transition-all text-xs font-bold border ${
                  isSelected
                    ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-400 text-amber-300 shadow-md scale-105'
                    : isDayMode 
                      ? 'border-transparent text-slate-600 hover:bg-slate-100'
                      : 'border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                <span className="text-base">{planet.icon}</span>
                <span>{planet.name.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Active Tab View */}
        <div className="flex-1 flex flex-col">
          {activeTab === 'orrery' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 flex-1 items-stretch">
              
              {/* 3D Planet Visualizer Stage (Left 7 cols) */}
              <div className={`lg:col-span-7 rounded-3xl border p-6 flex flex-col justify-between relative overflow-hidden min-h-[380px] lg:min-h-[500px] ${
                isDayMode 
                  ? 'bg-gradient-to-b from-sky-50 to-indigo-100 border-slate-200 shadow-inner' 
                  : 'bg-gradient-to-b from-[#060a14] via-[#091124] to-[#040810] border-slate-800 shadow-2xl'
              }`}>
                {/* Background Cosmic Starfield / Grid */}
                <div className="absolute inset-0 opacity-20 pointer-events-none bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]" />

                {/* Subtle Orbital Rings */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                  <div className="w-[280px] h-[280px] rounded-full border border-sky-400/40 border-dashed animate-spin [animation-duration:120s]" />
                  <div className="w-[420px] h-[420px] rounded-full border border-amber-400/30 border-dashed animate-spin [animation-duration:180s]" />
                </div>

                {/* Top Badge Info */}
                <div className="flex items-center justify-between z-10">
                  <div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/40">
                      {selectedPlanet.type}
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-black mt-1 text-slate-100">
                      {selectedPlanet.name}
                    </h2>
                    <p className="text-xs text-amber-300 font-medium">
                      "{selectedPlanet.tagline}"
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] uppercase text-slate-400 font-bold block">Distance From Sun</span>
                    <span className="text-sm font-black text-sky-300">{selectedPlanet.distanceFromSun}</span>
                  </div>
                </div>

                {/* Center 3D Celestial Body Sphere */}
                <div className="my-auto flex flex-col items-center justify-center relative py-8 z-10">
                  <motion.div
                    key={selectedPlanet.id}
                    initial={{ scale: 0.7, opacity: 0, rotate: -20 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    transition={{ duration: 0.6, type: 'spring' }}
                    className="relative flex items-center justify-center"
                  >
                    {/* Atmospheric Glow */}
                    <div 
                      className="absolute rounded-full blur-2xl transition-all duration-700 pointer-events-none"
                      style={{
                        width: `${190 * selectedPlanet.scaleRatio}px`,
                        height: `${190 * selectedPlanet.scaleRatio}px`,
                        backgroundColor: selectedPlanet.glowColor
                      }}
                    />

                    {/* Planet Sphere */}
                    <div 
                      className={`rounded-full bg-gradient-to-br ${selectedPlanet.colorGradient} shadow-2xl relative flex items-center justify-center border border-white/20`}
                      style={{
                        width: `${170 * selectedPlanet.scaleRatio}px`,
                        height: `${170 * selectedPlanet.scaleRatio}px`,
                        boxShadow: `inset -25px -25px 40px rgba(0,0,0,0.8), 0 0 50px ${selectedPlanet.glowColor}`
                      }}
                    >
                      {/* Saturn Ring Simulation */}
                      {selectedPlanet.id === 'saturn' && (
                        <div className="absolute w-[280px] h-[55px] rounded-[100%] border-[12px] border-amber-200/50 border-double rotate-[-22deg] pointer-events-none shadow-lg" />
                      )}

                      {/* Surface Texture Shadow Details */}
                      <span className="text-5xl sm:text-6xl opacity-40 select-none">
                        {selectedPlanet.icon}
                      </span>
                    </div>
                  </motion.div>

                  <div className="mt-6 text-center">
                    <span className="text-xs px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300">
                      Diameter: <strong>{selectedPlanet.diameter}</strong>
                    </span>
                  </div>
                </div>

                {/* Bottom STEM Highlight Bar */}
                <div className="p-3 rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md flex items-center justify-between gap-3 z-10">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🇮🇳</span>
                    <div>
                      <span className="text-[10px] text-amber-400 font-bold uppercase block">ISRO Space Expedition</span>
                      <p className="text-xs font-semibold text-slate-200">{selectedPlanet.isroMission}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('quiz')}
                    className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-black text-xs hover:brightness-110 shadow-md"
                  >
                    Challenge Quiz ➜
                  </button>
                </div>
              </div>

              {/* Planet Data Cards (Right 5 cols) */}
              <div className="lg:col-span-5 flex flex-col gap-4">
                
                {/* STEM Science Metrics Grid */}
                <div className={`p-5 rounded-3xl border flex-1 flex flex-col justify-between gap-4 ${
                  isDayMode ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-900/80 border-slate-800 shadow-xl'
                }`}>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-wider text-amber-400 flex items-center gap-2 mb-3">
                      <SafeIcon icon={FiCompass} />
                      Astrophysics Telemetry
                    </h3>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div className={`p-3 rounded-2xl border ${isDayMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-800/60 border-slate-700/60'}`}>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Year Length (Orbit)</span>
                        <span className="text-sm font-black text-sky-400">{selectedPlanet.orbitDays}</span>
                      </div>

                      <div className={`p-3 rounded-2xl border ${isDayMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-800/60 border-slate-700/60'}`}>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Day Length (Spin)</span>
                        <span className="text-sm font-black text-emerald-400">{selectedPlanet.dayLength}</span>
                      </div>

                      <div className={`p-3 rounded-2xl border ${isDayMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-800/60 border-slate-700/60'}`}>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Surface Gravity</span>
                        <span className="text-sm font-black text-purple-400">{selectedPlanet.gravity}</span>
                      </div>

                      <div className={`p-3 rounded-2xl border ${isDayMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-800/60 border-slate-700/60'}`}>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Temperature</span>
                        <span className="text-sm font-black text-amber-400">{selectedPlanet.temp}</span>
                      </div>
                    </div>
                  </div>

                  {/* Moons Count Card */}
                  <div className={`p-4 rounded-2xl border flex items-center justify-between ${
                    isDayMode ? 'bg-amber-50/60 border-amber-200' : 'bg-amber-500/10 border-amber-500/30'
                  }`}>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-amber-400 block">Natural Satellites & Moons</span>
                      <p className="text-sm font-black text-slate-200">{selectedPlanet.moons}</p>
                    </div>
                    <span className="text-2xl">🌕</span>
                  </div>

                  {/* "Did You Know?" Fact Box */}
                  <div className={`p-4 rounded-2xl border ${
                    isDayMode ? 'bg-indigo-50/60 border-indigo-200' : 'bg-indigo-950/40 border-indigo-800/60'
                  }`}>
                    <div className="flex items-center gap-1.5 text-indigo-400 text-xs font-black uppercase mb-1">
                      <SafeIcon icon={FiZap} />
                      STEM Discovery Fact
                    </div>
                    <p className="text-xs leading-relaxed text-slate-300">
                      {selectedPlanet.funFact}
                    </p>
                  </div>

                  {/* Quick Action to Telemetry Compare */}
                  <button
                    onClick={() => setActiveTab('telemetry')}
                    className={`w-full py-2.5 rounded-xl border text-xs font-bold transition-all text-center ${
                      isDayMode ? 'bg-slate-100 hover:bg-slate-200 border-slate-300 text-slate-700' : 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300'
                    }`}
                  >
                    Compare All 8 Planets Side-by-Side ➜
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Full Telemetry Comparison Table */}
          {activeTab === 'telemetry' && (
            <div className={`p-5 rounded-3xl border overflow-x-auto ${
              isDayMode ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-900/80 border-slate-800 shadow-xl'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-black text-slate-100">All-Planets STEM Comparison Chart</h3>
                  <p className="text-xs text-slate-400">Comprehensive Solar System physical mechanics & ISRO missions</p>
                </div>
                <button
                  onClick={() => setActiveTab('orrery')}
                  className="px-3 py-1 rounded-xl bg-amber-500 text-slate-950 font-bold text-xs"
                >
                  Return to 3D Orrery
                </button>
              </div>

              <table className="w-full text-left text-xs min-w-[700px]">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400 font-bold uppercase text-[10px]">
                    <th className="py-2.5 px-3">Celestial Body</th>
                    <th className="py-2.5 px-3">Distance From Sun</th>
                    <th className="py-2.5 px-3">Diameter</th>
                    <th className="py-2.5 px-3">Gravity</th>
                    <th className="py-2.5 px-3">Year Length</th>
                    <th className="py-2.5 px-3">Moons</th>
                    <th className="py-2.5 px-3">Key Indian ISRO / Space Mission</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 font-medium">
                  {CELESTIAL_BODIES.map((b) => (
                    <tr 
                      key={b.id} 
                      onClick={() => { setSelectedPlanet(b); setActiveTab('orrery'); }}
                      className="hover:bg-amber-500/10 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-3 flex items-center gap-2 font-bold text-slate-200">
                        <span>{b.icon}</span>
                        <span>{b.name}</span>
                      </td>
                      <td className="py-3 px-3 text-sky-400">{b.distanceFromSun}</td>
                      <td className="py-3 px-3 text-slate-300">{b.diameter}</td>
                      <td className="py-3 px-3 text-purple-400">{b.gravity}</td>
                      <td className="py-3 px-3 text-emerald-400">{b.orbitDays}</td>
                      <td className="py-3 px-3 text-amber-300">{b.moons}</td>
                      <td className="py-3 px-3 text-slate-300 font-bold">{b.isroMission}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Interactive Cadet Quiz Mode */}
          {activeTab === 'quiz' && (
            <div className={`p-6 sm:p-8 rounded-3xl border max-w-2xl mx-auto w-full text-center flex flex-col justify-between ${
              isDayMode ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-900/90 border-slate-800 shadow-2xl'
            }`}>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-black uppercase border border-amber-500/30">
                    Question {quizIndex + 1} of {STEM_QUIZ_QUESTIONS.length}
                  </span>
                  <span className="text-xs font-bold text-slate-400">
                    Cadet Score: <strong className="text-amber-400">{quizScore}</strong>
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-black text-slate-100 mb-6 leading-snug">
                  {STEM_QUIZ_QUESTIONS[quizIndex].q}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {STEM_QUIZ_QUESTIONS[quizIndex].options.map((opt, idx) => {
                    const isPicked = selectedOption === idx;
                    const isRight = idx === STEM_QUIZ_QUESTIONS[quizIndex].correctIndex;
                    let btnStyle = isDayMode 
                      ? 'bg-slate-50 border-slate-200 text-slate-800 hover:bg-slate-100' 
                      : 'bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700';

                    if (showAnswerFeedback) {
                      if (isRight) {
                        btnStyle = 'bg-emerald-600 border-emerald-400 text-white font-black scale-102';
                      } else if (isPicked) {
                        btnStyle = 'bg-rose-600 border-rose-400 text-white font-black';
                      }
                    }

                    return (
                      <button
                        key={idx}
                        disabled={showAnswerFeedback}
                        onClick={() => handleAnswerSubmit(idx)}
                        className={`p-4 rounded-2xl border text-sm font-bold text-left transition-all ${btnStyle}`}
                      >
                        <span className="inline-block w-6 h-6 rounded-full bg-black/20 text-center leading-6 mr-2 text-xs font-black">
                          {String.fromCharCode(65 + idx)}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>

                {showAnswerFeedback && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 font-medium"
                  >
                    💡 <strong>Astronomer's Note:</strong> {STEM_QUIZ_QUESTIONS[quizIndex].insight}
                  </motion.div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-800 flex justify-between items-center text-xs text-slate-400">
                <span>Pass all 3 questions to unlock the Junior Galactic Explorer Certificate!</span>
                <button
                  onClick={() => setActiveTab('orrery')}
                  className="text-amber-400 hover:underline font-bold"
                >
                  Back to Orrery
                </button>
              </div>
            </div>
          )}

          {/* Certificate & Voucher Pass Tab */}
          {activeTab === 'pass' && (
            <div className="max-w-xl mx-auto w-full">
              {!hasClaimedPass ? (
                <div className={`p-6 sm:p-8 rounded-3xl border text-center ${
                  isDayMode ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-900/90 border-slate-800 shadow-2xl'
                }`}>
                  <ConfettiEffect />
                  <span className="text-5xl block mb-2">🎖️</span>
                  <h3 className="text-xl sm:text-2xl font-black text-amber-400">
                    Quiz Completed! Congratulations!
                  </h3>
                  <p className="text-xs text-slate-300 mt-1 mb-6">
                    Enter your name to generate your personalized <strong>Junior Galactic Space Explorer Certificate & Voucher Pass</strong>!
                  </p>

                  <form onSubmit={handleGenerateCertificate} className="flex flex-col gap-3 text-left">
                    <div>
                      <label className="text-xs font-bold text-slate-400 block mb-1">Student's Full Name *</label>
                      <input
                        type="text"
                        required
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        placeholder="e.g. Aryan Sharma"
                        className={`w-full p-3 rounded-xl border text-sm font-bold focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                          isDayMode ? 'bg-slate-50 border-slate-300 text-slate-800' : 'bg-slate-800 border-slate-700 text-white'
                        }`}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-slate-400 block mb-1">School / Institution</label>
                        <input
                          type="text"
                          value={schoolName}
                          onChange={(e) => setSchoolName(e.target.value)}
                          placeholder="e.g. DPS International"
                          className={`w-full p-3 rounded-xl border text-sm font-bold focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                            isDayMode ? 'bg-slate-50 border-slate-300 text-slate-800' : 'bg-slate-800 border-slate-700 text-white'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-400 block mb-1">Grade / Class</label>
                        <select
                          value={gradeClass}
                          onChange={(e) => setGradeClass(e.target.value)}
                          className={`w-full p-3 rounded-xl border text-sm font-bold focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                            isDayMode ? 'bg-slate-50 border-slate-300 text-slate-800' : 'bg-slate-800 border-slate-700 text-white'
                          }`}
                        >
                          <option>Grade 3</option>
                          <option>Grade 4</option>
                          <option>Grade 5</option>
                          <option>Grade 6</option>
                          <option>Grade 7</option>
                          <option>Grade 8</option>
                          <option>Grade 9+</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="mt-3 w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950 font-black text-sm uppercase tracking-wider hover:brightness-110 shadow-lg shadow-amber-500/20"
                    >
                      Issue Official Certificate & Voucher 📜
                    </button>
                  </form>
                </div>
              ) : (
                /* The Official Printable/Display Pass */
                <div className="flex flex-col gap-4">
                  <div className="p-6 sm:p-8 rounded-3xl border-2 border-amber-400 bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-950 text-white shadow-2xl relative overflow-hidden">
                    <div className="flex justify-between items-start border-b border-amber-500/40 pb-4 mb-4">
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 block">
                          Cosmic Academy of Astronomy & STEM
                        </span>
                        <h2 className="text-xl sm:text-2xl font-black text-white">
                          Certified Galactic Navigator
                        </h2>
                      </div>
                      <span className="text-4xl">🚀</span>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Astrophysics Cadet:</span>
                        <strong className="text-amber-300 text-sm">{studentName}</strong>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Academy / School:</span>
                        <strong className="text-slate-200">{schoolName || 'Stall Visitor'} ({gradeClass})</strong>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Achievement:</span>
                        <strong className="text-emerald-400">Mastered 8 Planets & Solar Physics</strong>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Prize Voucher:</span>
                        <span className="font-mono font-black text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded border border-amber-500/40">
                          {voucherCode}
                        </span>
                      </div>
                    </div>

                    <div className="p-3 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiAward} className="text-amber-400 text-xl" />
                        <div>
                          <p className="text-[11px] font-bold text-slate-200">Redeemable at Stall / Event Desk</p>
                          <p className="text-[10px] text-slate-400">Collect special Space Badge / Glow Pen</p>
                        </div>
                      </div>
                      <span className="text-xs font-mono font-bold text-amber-300">VALID TODAY</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => window.print()}
                      className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-xs uppercase hover:bg-amber-400 transition-all text-center"
                    >
                      🖨️ Print Pass
                    </button>
                    <button
                      onClick={() => {
                        setHasClaimedPass(false);
                        setActiveTab('orrery');
                        setQuizIndex(0);
                        setQuizScore(0);
                      }}
                      className="px-4 py-3 rounded-xl border border-slate-700 bg-slate-800 text-slate-300 text-xs font-bold hover:bg-slate-700"
                    >
                      Explore More Planets ➜
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
